import type { SelectHTMLAttributes } from 'react';

interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  label?: string;
  icon?: string;
  error?: string;
  options: { value: string; label: string }[];
  placeholder?: string;
}

export default function Select({
  label,
  icon,
  error,
  options,
  placeholder,
  className = '',
  id,
  ...rest
}: SelectProps) {
  const selectId = id ?? rest.name ?? label?.replace(/\s+/g, '-').toLowerCase();
  return (
    <div className="w-full min-w-0">
      {label ? (
        <label
          htmlFor={selectId}
          className="block mb-2 text-[13px] font-semibold text-foreground-700 font-label"
        >
          {label}
        </label>
      ) : null}
      <div className="relative">
        {icon ? (
          <span className="pointer-events-none absolute inset-y-0 left-3.5 flex items-center justify-center w-4 h-4 text-foreground-400">
            <i className={`${icon} text-base`} aria-hidden="true" />
          </span>
        ) : null}
        <select
          id={selectId}
          className={`w-full box-border h-12 appearance-none rounded-xl border bg-background-50 text-[15px] text-foreground-900 transition-colors outline-none cursor-pointer ${icon ? 'pl-10' : 'pl-4'} pr-10 ${
            error
              ? 'border-accent-400 focus:border-accent-500'
              : 'border-background-300 hover:border-background-400 focus:border-primary-500'
          } ${className}`}
          {...rest}
        >
          {placeholder ? <option value="">{placeholder}</option> : null}
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        <span className="pointer-events-none absolute inset-y-0 right-3.5 flex items-center justify-center w-4 h-4 text-foreground-400">
          <i className="ri-arrow-down-s-line text-lg" aria-hidden="true" />
        </span>
      </div>
      {error ? (
        <p className="mt-1.5 flex items-start gap-1.5 text-[12.5px] text-accent-700">
          <span className="flex items-center justify-center w-3.5 h-3.5 mt-0.5 shrink-0">
            <i className="ri-error-warning-line text-[13px]" aria-hidden="true" />
          </span>
          <span>{error}</span>
        </p>
      ) : null}
    </div>
  );
}